import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Permission, PurchaseStatus, isOpenPurchaseStatus, resolvePreset } from '@construction/shared';
import { api } from '@/api/client';
import {
  Alert,
  Button,
  Card,
  DataTable,
  EmptyState,
  PageHeader,
  RowActions,
  Spinner,
  StatStrip,
  TableRow,
} from '@/components/ui';
import { DownloadButton } from '@/features/exports/DownloadButton';
import { MyPurchasesList } from '@/features/purchases/MyPurchasesList';
import { PurchaseFilters, purchaseEmptyKey } from '@/features/purchases/PurchaseFilters';
import {
  PurchaseDetail,
  PurchaseList,
  PurchaseStatusBadge,
  ReceiptThumb,
  day,
  useReviewPurchase,
} from '@/features/purchases/PurchaseViews';
import { defaultPurchaseStatus } from '@/features/purchases/default-status';
import { PurchaseForm } from '@/features/purchases/PurchaseForm';
import { RecordFlag } from '@/features/records/RecordTime';
import { RecordTabs } from '@/features/records/RecordTabs';
import { QueueBar } from '@/features/upload-queue';
import { formatMoney } from '@/lib/format';
import { useErrorMessage } from '@/hooks/useErrorMessage';
import { siteDateKey } from '@/lib/site-time';
import { useMobileSite } from '@/hooks/useCurrentSite';
import { useRecordTab } from '@/hooks/useRecordTab';
import { usePermissions } from '@/lib/permissions';
import { useAuthStore } from '@/store/auth';

interface Purchase {
  id: string;
  siteId: number;
  purchasedAt: string;
  vendor: string | null;
  category: string | null;
  amountCents: number;
  currency: string;
  note: string | null;
  status: PurchaseStatus;
  reviewNote: string | null;
  reviewedAt: string | null;
  createdAt: string;
  user: { id: number; employeeNo: string; name: string };
  receipts: Array<{
    id: string;
    url: string;
    thumbUrl: string | null;
    takenAt?: string | null;
    uploadedAt?: string;
  }>;
}

interface Summary {
  /** 四种状态各有几笔、各多少钱。由服务端聚合，不受列表条数上限影响（CR-031） */
  byStatus: Record<string, { count: number; amountCents: number }>;
  /** 待批准 / 待报销不限日期的笔数和金额（CR-049）：这两个页签的数字和汇总用它 */
  open: Record<string, { count: number; amountCents: number }>;
  // 以下只算已批准 + 已报销
  totalCents: number;
  count: number;
  byCategory: Array<{ category: string; amountCents: number; count: number }>;
  byVendor: Array<{ vendor: string; amountCents: number; count: number }>;
  byUser: Array<{
    userId: number;
    name: string;
    employeeNo: string;
    amountCents: number;
    count: number;
    /** 批了还没报的 */
    outstandingCents: number;
  }>;
  /** 有采购的那几天（工地时区），日期升序 */
  byDay: Array<{ date: string; amountCents: number; count: number }>;
  noReceiptCount: number;
  noReceiptCents: number;
}

/**
 * 采购页（CR-042：一页两个 tab，两套外壳共用；CR-082 按示例重做）。
 *
 * 客户原话：「采购 / 我的采购也可以整合成一样的」。
 * - 有 `purchase.write` → 页头「记一笔」+「我的」tab（我在这个工地记过的）；
 * - 有 `purchase.view` → 「本工地」tab；两个都有才显示「本工地 / 我的」，**默认「本工地」**（CR-048）。
 *
 * 从上到下（CR-082）：页头（下载 · 记一笔）→ 状态页签（待批准 N ｜ 待报销 N ｜ 已报销 ｜ 已驳回 ｜ 全部记录，CR-050）
 * → 工具条（本工地 / 我的 + 日期胶囊，右边一行汇总）→ 统计条（只在查记录的页签）→ 表格 / 列表 → 批量条。
 * **常用的审批放在行上**：待批准「驳回 · 批准」、待报销「标记已报销」；驳回要写原因，点了打开详情、光标在意见框里。
 *
 * **「记一笔」在页面上，不跟着 tab 走**（CR-048）；拍小票被系统回收后（`?compose=1`）表单也得在。
 * 记到的永远是当前工地（CR-042 推翻 CR-015 的豁免）：「只能为当前工地买材料。如果还需要为其他工地买材料，可以在备注里填写」。
 */
export function PurchasesPage() {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const { can } = usePermissions();
  const canWrite = can(Permission.PURCHASE_WRITE);
  const canView = can(Permission.PURCHASE_VIEW);
  const canExport = can(Permission.PURCHASE_EXPORT);

  const { site, isLoading } = useMobileSite();
  const { tab, showTabs, setTab } = useRecordTab({ canMine: canWrite, canSite: canView });

  // `?compose=1`：拍小票途中被安卓回收之后，恢复层把人送回这个地址，
  // 表单要自己展开，否则他看到的还是那张收起来的列表（CR-037）
  const [params] = useSearchParams();
  const [creating, setCreating] = useState(() => params.get('compose') === '1');

  // 两个 tab 共用的筛选（CR-048）。采购按月看：报销周期是月度的，「今日」对它没意义
  const tz = site?.timezone ?? 'America/Los_Angeles';
  const today = siteDateKey(tz);
  const [range, setRange] = useState(() => resolvePreset('thisMonth', today));
  // 状态页签（CR-050）。`undefined` = 还没选：「本工地」按 `defaultPurchaseStatus` 落点，「我的」落全部记录。
  // `?status=` 优先：工地概览「待处理」和 App 待办点进来的（CR-049）
  const [status, setStatus] = useState<PurchaseStatus | null | undefined>(() => {
    const requested = params.get('status');
    return requested !== null && (Object.values(PurchaseStatus) as string[]).includes(requested)
      ? (requested as PurchaseStatus)
      : undefined;
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-20 text-ink-3">
        <Spinner />
      </div>
    );
  }
  if (!site) {
    return <EmptyState title={t('site.empty')} hint={t('site.emptyHintWorker')} />;
  }

  // 待批准 / 待报销导全部（CR-049），和列表一致；其余跟着区间
  const current = status ?? null;
  const open = isOpenPurchaseStatus(current);
  const recordTabs = showTabs ? <RecordTabs tab={tab} onChange={setTab} /> : null;

  return (
    <>
      <PageHeader
        title={t('nav.purchases')}
        description={site.name}
        action={
          <>
            {/* 下载表格 + 小票，跟着当前区间和状态（CR-043）。「我的」没有下载 */}
            {tab === 'site' && canExport && status !== undefined && (
              <DownloadButton
                path={`/sites/${site.id}/exports/purchases`}
                query={{ ...(open ? {} : { from: range.from, to: range.to }), status: current ?? undefined }}
                label={t('export.download')}
                action="purchase.export"
              />
            )}
            {canWrite && (
              <Button onClick={() => setCreating(true)} data-action="purchases.add">
                {t('purchases.add')}
              </Button>
            )}
          </>
        }
      />

      {canWrite && (
        <div className="mb-4">
          <QueueBar />
        </div>
      )}

      {creating && user && (
        <PurchaseForm
          site={site}
          userId={user.id}
          // 重新拉列表没有意义：这条刚进队列，服务端还不知道它（CR-037）。
          // 队列真的传完之后 App.tsx 会让 `my-purchases` 失效。切到「我的」，那条「上传中」在那边
          onDone={() => {
            setCreating(false);
            if (showTabs) setTab('mine');
          }}
          onCancel={() => setCreating(false)}
        />
      )}

      {tab === 'mine' ? (
        <MyPurchasesList
          site={site}
          composing={creating}
          range={range}
          onRangeChange={setRange}
          // 工人不审批，不自动落待处理：「全部记录」加页签上的数字已经回答「我垫的钱报了没有」
          status={status ?? null}
          onStatusChange={setStatus}
          today={today}
          leading={recordTabs}
        />
      ) : (
        <SitePurchases
          site={site}
          canReview={can(Permission.PURCHASE_REVIEW)}
          range={range}
          onRangeChange={setRange}
          status={status}
          onStatusChange={setStatus}
          today={today}
          leading={recordTabs}
        />
      )}
    </>
  );
}

/** 「本工地」tab（CR-031 那一页，CR-082 重做）。筛选状态由页面给（CR-048） */
function SitePurchases({
  site,
  canReview,
  range,
  onRangeChange,
  status,
  onStatusChange,
  today,
  leading,
}: {
  site: { id: number; name: string; timezone: string; currency: string };
  canReview: boolean;
  range: { from: string; to: string };
  onRangeChange: (range: { from: string; to: string }) => void;
  /** `undefined` = 还没选，按 `defaultPurchaseStatus` 落点（CR-050） */
  status: PurchaseStatus | null | undefined;
  onStatusChange: (status: PurchaseStatus | null) => void;
  today: string;
  leading: ReactNode;
}) {
  const { t, i18n } = useTranslation();
  const toMessage = useErrorMessage();
  const tz = site.timezone;
  const locale = i18n.language;
  const review = useReviewPurchase();

  const [openId, setOpenId] = useState<string | null>(null);
  const [focusNote, setFocusNote] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [breakdown, setBreakdown] = useState(false);
  const [rowError, setRowError] = useState<string | null>(null);

  const summary = useQuery({
    queryKey: ['purchase-summary', site.id, range.from, range.to],
    queryFn: () => api.get<Summary>(`/sites/${site.id}/purchases/summary`, { from: range.from, to: range.to }),
  });
  const openCounts = summary.data?.open;

  // 默认落点（CR-050）：能审批的人落在有事的页签，其余落全部记录。落定之后写进状态 ——
  // 批完最后一笔待批准，页签不会自己跳走；人一点页签就以他点的为准
  useEffect(() => {
    if (status !== undefined) return;
    const next = summary.isError ? null : defaultPurchaseStatus(canReview, openCounts);
    if (next !== undefined) onStatusChange(next);
  }, [status, canReview, openCounts, summary.isError, onStatusChange]);

  const deciding = status === undefined;
  const current = status ?? null;

  // 待批准 / 待报销不传区间（CR-049）：服务端全部列出、最早的在前。区间变了也不用重查
  const open = isOpenPurchaseStatus(current);
  const purchases = useQuery({
    queryKey: ['purchases', site.id, open ? null : range.from, open ? null : range.to, current],
    queryFn: () =>
      api.get<Purchase[]>(`/sites/${site.id}/purchases`, {
        ...(open ? {} : { from: range.from, to: range.to }),
        ...(current ? { status: current } : {}),
      }),
    enabled: !deciding,
  });

  const currency = site.currency;
  const list = purchases.data ?? [];
  const opened = list.find((p) => p.id === openId) ?? null;
  const money = (cents: number) => formatMoney(cents, locale, currency);

  // 批量报销只在「待报销」页签里有意义；它也是审批动作，只看采购的人没有勾选框（CR-040）
  const canBatch = current === PurchaseStatus.APPROVED && canReview;
  // 行上的审批按钮只在两个待处理的页签里（CR-082）
  const inlineActions = open && canReview;

  const toggleSelected = (id: string, on: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });

  const openDetail = (id: string, note = false) => {
    setFocusNote(note);
    setOpenId(id);
  };

  const quick = (purchase: Purchase, target: PurchaseStatus) => {
    setRowError(null);
    review.mutate(
      { id: purchase.id, status: target },
      { onError: (err) => setRowError(`${purchase.vendor ?? ''} ${money(purchase.amountCents)}：${toMessage(err)}`) },
    );
  };

  // 汇总一行：数字来自服务端聚合（CR-031），不数当前列表 —— 列表有 300 条上限
  const figure = (() => {
    const data = summary.data;
    if (!data) return null;
    if (current && open) return data.open[current] ?? { count: 0, amountCents: 0 };
    if (current) return data.byStatus[current] ?? { count: 0, amountCents: 0 };
    return Object.values(data.byStatus).reduce(
      (sum, row) => ({ count: sum.count + row.count, amountCents: sum.amountCents + row.amountCents }),
      { count: 0, amountCents: 0 },
    );
  })();

  return (
    <>
      {/* 和「我的」同一套页签 + 工具条（CR-048 / CR-050），页签上的数字来自服务端汇总 */}
      <PurchaseFilters
        range={range}
        onRangeChange={onRangeChange}
        today={today}
        status={current}
        onStatusChange={(next) => {
          onStatusChange(next);
          // 换页签就清空勾选：勾着的那几条已经不在这一屏里了
          setSelected(new Set());
        }}
        // 只有待批准 / 待报销带数字，不限日期（CR-049 / CR-050）
        counts={{ PENDING: openCounts?.PENDING?.count, APPROVED: openCounts?.APPROVED?.count }}
        leading={leading}
        summary={
          figure && figure.count > 0 && t('purchases.summaryLine', { count: figure.count, amount: money(figure.amountCents) })
        }
      />

      {/* 统计只在查记录的页签下出现（CR-050）：它说的是「这段时间」，待处理的页签不看日期 */}
      {summary.data && !deciding && !open && (countAll(summary.data.byStatus) ?? 0) > 0 && (
        <div className="mb-4">
          <StatStrip
            items={[
              {
                key: 'total',
                label: t('purchases.approvedTotal'),
                value: money(summary.data.totalCents),
                hint: t('purchases.countLabel', { count: summary.data.count }),
              },
              {
                key: 'category',
                label: t('purchases.topCategories'),
                value: summary.data.byCategory[0]?.category || '—',
                hint: summary.data.byCategory[0] && money(summary.data.byCategory[0].amountCents),
              },
              {
                key: 'vendor',
                label: t('purchases.topVendors'),
                value: summary.data.byVendor[0]?.vendor || '—',
                hint: summary.data.byVendor[0] && money(summary.data.byVendor[0].amountCents),
              },
              {
                key: 'receipt',
                label: t('purchases.noReceiptCount'),
                value: t('purchases.countLabel', { count: summary.data.noReceiptCount }),
                hint:
                  summary.data.noReceiptCount > 0 ? money(summary.data.noReceiptCents) : t('purchases.allHaveReceipts'),
                tone: summary.data.noReceiptCount > 0 ? 'danger' : 'default',
              },
            ]}
          />
          <div className="mt-2 flex justify-end">
            <button
              type="button"
              aria-expanded={breakdown}
              onClick={() => setBreakdown((v) => !v)}
              className="inline-flex min-h-touch-sm items-center rounded-md px-2 text-caption font-medium text-brand-600 hover:bg-brand-50"
            >
              {breakdown ? t('purchases.hideBreakdown') : t('purchases.showBreakdown')}
            </button>
          </div>
          {breakdown && (
            <SummaryBreakdown summary={summary.data} currency={currency} locale={locale} from={range.from} to={range.to} />
          )}
        </div>
      )}

      {rowError && (
        <div className="mb-3">
          <Alert>{rowError}</Alert>
        </div>
      )}

      {(deciding || purchases.isLoading) && (
        <div className="flex justify-center py-16 text-ink-3">
          <Spinner />
        </div>
      )}
      {purchases.isError && <Alert>{toMessage(purchases.error)}</Alert>}
      {!deciding && !purchases.isLoading && list.length === 0 && <EmptyState title={t(purchaseEmptyKey(current))} />}

      {list.length > 0 && (
        <>
          {/* 窄屏：列表（CR-036） */}
          <PurchaseList
            purchases={list}
            timezone={tz}
            onOpen={(id) => openDetail(id)}
            selectable={canBatch}
            selected={selected}
            onSelect={toggleSelected}
            className="xl:hidden"
          />

          {/* 电脑：表格 */}
          <DataTable
            minWidth="60rem"
            columns={[
              ...(canBatch
                ? [
                    {
                      key: 'check',
                      className: 'w-10',
                      label: (
                        <input
                          type="checkbox"
                          aria-label={t('purchases.selectAll')}
                          checked={selected.size > 0 && selected.size === list.length}
                          onChange={(e) => setSelected(e.target.checked ? new Set(list.map((p) => p.id)) : new Set())}
                          className="size-4 accent-brand-600"
                        />
                      ),
                    },
                  ]
                : []),
              { key: 'date', label: t('purchases.date'), className: 'w-32' },
              { key: 'user', label: t('logs.submitter'), className: 'w-28' },
              { key: 'category', label: t('purchases.category'), className: 'w-28' },
              { key: 'vendor', label: `${t('purchases.vendor')} · ${t('purchases.note')}` },
              { key: 'receipt', label: t('purchases.receipt'), className: 'w-24' },
              { key: 'amount', label: t('purchases.amount'), align: 'right' as const, className: 'w-32' },
              { key: 'status', label: inlineActions ? '' : t('user.status'), align: 'right' as const, className: inlineActions ? 'w-48' : 'w-28' },
            ]}
          >
            {list.map((purchase) => (
              <TableRow key={purchase.id} selected={openId === purchase.id} onOpen={() => openDetail(purchase.id)}>
                {canBatch && (
                  // 勾选不该顺带打开详情
                  <td onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selected.has(purchase.id)}
                      onChange={(e) => toggleSelected(purchase.id, e.target.checked)}
                      aria-label={t('purchases.selectOne')}
                      className="size-4 accent-brand-600"
                    />
                  </td>
                )}
                <td className="whitespace-nowrap tabular-nums text-ink-2">
                  <span className="inline-flex items-center gap-2">
                    {day(purchase.purchasedAt, locale, tz)}
                    <RecordFlag recordedAt={purchase.createdAt} timeZone={tz} businessDay={purchase.purchasedAt} />
                  </span>
                </td>
                <td className="whitespace-nowrap text-ink-2">{purchase.user.name}</td>
                <td className="whitespace-nowrap">{purchase.category}</td>
                {/* 商家 + 备注吃掉剩余宽度，长了才截（CR-035：不挤压） */}
                <td className="truncate">
                  <span className="font-medium text-ink">{purchase.vendor || t('purchases.noVendor')}</span>
                  {purchase.note && <span className="text-ink-3"> · {purchase.note}</span>}
                </td>
                <td className="whitespace-nowrap">
                  {purchase.receipts[0] ? (
                    <span className="inline-flex items-center gap-1.5">
                      <ReceiptThumb url={purchase.receipts[0].thumbUrl ?? purchase.receipts[0].url} className="h-8 w-6 rounded border border-rule object-cover" />
                      {purchase.receipts.length > 1 && <span className="text-caption text-ink-3">+{purchase.receipts.length - 1}</span>}
                    </span>
                  ) : (
                    <span className="text-caption text-safety-600">{t('purchases.noReceiptCount')}</span>
                  )}
                </td>
                <td className="whitespace-nowrap text-right font-semibold tabular-nums text-ink">{money(purchase.amountCents)}</td>
                <td className="whitespace-nowrap text-right">
                  {inlineActions ? (
                    <RowActions>
                      {purchase.status === PurchaseStatus.PENDING && (
                        <>
                          <Button size="sm" variant="dangerOutline" onClick={() => openDetail(purchase.id, true)}>
                            {t('purchases.reject')}
                          </Button>
                          <Button
                            size="sm"
                            variant="secondary"
                            loading={review.isPending && review.variables?.id === purchase.id}
                            onClick={() => quick(purchase, PurchaseStatus.APPROVED)}
                          >
                            {t('purchases.approve')}
                          </Button>
                        </>
                      )}
                      {purchase.status === PurchaseStatus.APPROVED && (
                        <Button
                          size="sm"
                          variant="secondary"
                          loading={review.isPending && review.variables?.id === purchase.id}
                          onClick={() => quick(purchase, PurchaseStatus.REIMBURSED)}
                        >
                          {t('purchases.markReimbursed')}
                        </Button>
                      )}
                    </RowActions>
                  ) : (
                    <PurchaseStatusBadge status={purchase.status} />
                  )}
                </td>
              </TableRow>
            ))}
          </DataTable>
        </>
      )}

      {canBatch && selected.size > 0 && (
        <BatchBar
          ids={[...selected]}
          purchases={list}
          currency={currency}
          locale={locale}
          onClear={() => setSelected(new Set())}
          onDone={(failedIds) => setSelected(new Set(failedIds))}
        />
      )}

      <PurchaseDetail
        purchase={opened}
        timezone={tz}
        focusNote={focusNote}
        onClose={() => {
          setOpenId(null);
          setFocusNote(false);
        }}
      />
    </>
  );
}

function countAll(byStatus?: Record<string, { count: number }>): number | undefined {
  if (!byStatus) return undefined;
  return Object.values(byStatus).reduce((sum, row) => sum + row.count, 0);
}

/**
 * 底部吸附的批量条（CR-031，CR-082 改成浮起的一条）。
 *
 * **一条一条调现有的复核接口，不加批量接口**：服务端那边每一条都要校验状态流转
 * 和写审计日志，加一个批量端点等于把同样的逻辑再实现一遍。
 * 代价是**不是原子的** —— 中途失败会留下一部分已报销、一部分没有，
 * 所以失败的那几条要留在勾选状态里让人直接重试（和图纸批量上传同一种处理）。
 */
function BatchBar({
  ids,
  purchases,
  currency,
  locale,
  onClear,
  onDone,
}: {
  ids: string[];
  purchases: Purchase[];
  currency: string;
  locale: string;
  onClear: () => void;
  onDone: (failedIds: string[]) => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const toMessage = useErrorMessage();
  const [failed, setFailed] = useState<Array<{ id: string; reason: string }>>([]);

  const total = purchases.filter((p) => ids.includes(p.id)).reduce((sum, p) => sum + p.amountCents, 0);

  const run = useMutation({
    mutationFn: async () => {
      const bad: Array<{ id: string; reason: string }> = [];
      for (const id of ids) {
        try {
          await api.post(`/purchases/${id}/review`, { status: PurchaseStatus.REIMBURSED });
        } catch (err) {
          bad.push({ id, reason: toMessage(err) });
        }
      }
      return bad;
    },
    onSuccess: (bad) => {
      void queryClient.invalidateQueries({ queryKey: ['purchases'] });
      void queryClient.invalidateQueries({ queryKey: ['purchase-summary'] });
      void queryClient.invalidateQueries({ queryKey: ['overview'] });
      setFailed(bad);
      onDone(bad.map((b) => b.id));
    },
  });

  return (
    <div className="sticky bottom-3 z-nav mt-4 max-md:bottom-20">
      <Card className="px-4 py-3 shadow-pop">
        {failed.length > 0 && (
          <div className="mb-2">
            <Alert>
              <p>{t('purchases.batchFailed', { count: failed.length })}</p>
              <ul className="mt-1 flex flex-col gap-0.5">
                {failed.map((f) => {
                  const p = purchases.find((x) => x.id === f.id);
                  return (
                    <li key={f.id}>
                      {p ? formatMoney(p.amountCents, locale, p.currency) : f.id}：{f.reason}
                    </li>
                  );
                })}
              </ul>
            </Alert>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-3">
          <span className="text-body text-ink-2">
            {t('purchases.selectedCount', { count: ids.length })}
            <b className="ml-2 font-semibold tabular-nums text-ink">{formatMoney(total, locale, currency)}</b>
          </span>
          <span className="flex-1" />
          <Button variant="ghost" onClick={onClear}>
            {t('purchases.clearSelection')}
          </Button>
          <Button loading={run.isPending} onClick={() => run.mutate()}>
            {t('purchases.markReimbursed')}
          </Button>
        </div>
      </Card>
    </div>
  );
}

/** 占比写成整数百分比；有钱但不到 1% 的写「<1%」，不写 0% 骗人 */
const percent = (share: number) => (share > 0 && share < 1 ? '<1%' : `${Math.round(share)}%`);

/** 排行榜列前几名，其余并成「其他 N 家」。Zoho Expense 和 Expensify 的 Top 类别 / Top 商家都是前 10 */
const TOP_ROWS = 10;

function topWithRest<T extends { amountCents: number; count: number }>(rows: T[]) {
  // 只多出一家就不并 ——「其他 1 家」不如直接写出来
  if (rows.length <= TOP_ROWS + 1) return { top: rows, rest: null };
  const tail = rows.slice(TOP_ROWS);
  return {
    top: rows.slice(0, TOP_ROWS),
    rest: {
      groups: tail.length,
      amountCents: tail.reduce((sum, row) => sum + row.amountCents, 0),
      count: tail.reduce((sum, row) => sum + row.count, 0),
    },
  };
}

/** 超过这么多天就按月画，不然柱子细到看不清 */
const DAILY_LIMIT = 62;

/**
 * 把服务端给的「有采购的那几天」铺满整个区间，空的日子补零 ——
 * 趋势图上的空档本身就是信息（那几天没买东西）。区间太长就并成按月。
 */
function trendBuckets(byDay: Summary['byDay'], from: string, to: string) {
  const amounts = new Map(byDay.map((day) => [day.date, day]));
  const days: Array<{ key: string; amountCents: number; count: number }> = [];
  // 日期型的值按 UTC 走，不经过任何时区（代码约定里「日期型」那一条）
  for (let d = new Date(`${from}T00:00:00Z`); d <= new Date(`${to}T00:00:00Z`); d.setUTCDate(d.getUTCDate() + 1)) {
    const key = d.toISOString().slice(0, 10);
    const hit = amounts.get(key);
    days.push({ key, amountCents: hit?.amountCents ?? 0, count: hit?.count ?? 0 });
  }
  if (days.length <= DAILY_LIMIT) return { unit: 'day' as const, buckets: days };

  const months = new Map<string, { key: string; amountCents: number; count: number }>();
  for (const day of days) {
    const key = day.key.slice(0, 7);
    const month = months.get(key) ?? { key, amountCents: 0, count: 0 };
    month.amountCents += day.amountCents;
    month.count += day.count;
    months.set(key, month);
  }
  return { unit: 'month' as const, buckets: [...months.values()] };
}

/**
 * 采购统计。做法照搬成熟的报销系统，不自己发明
 * （Zoho Expense 管理员仪表盘、Expensify Insights）：
 *
 * 1. **默认收起**（客户要求）：CR-082 起收起时是一条统计（合计、最多的类别和商家、没小票的笔数），「看明细」才展开这里。
 * 2. 待批准 / 待报销**不在这里**（CR-050）：它们是页面上的页签，带着不限日期的数字；
 *    统计只在查记录的页签（已报销 / 已驳回 / 全部记录）下出现，一张卡只有一种口径。
 * 3. **没小票提醒**：对应 Zoho 的 Top Policy Violations —— 报销里最常见的违规。
 * 4. **支出趋势**：按天的柱状图，对应 Zoho 的 Spend Summary / Expensify 的 Spend over time。
 * 5. **Top 类别 / Top 商家**：从高到低排，金额 + 笔数，前 10 名（两家都是这样）。
 * 6. **按人员**：对应 Zoho 的 Top Spending Users，多一列「待报销」。
 *
 * 口径和 Zoho 一样写在标题旁边：只算已批准和已报销。
 *
 * **不用悬停承载数字**（NN/g 的 tooltip 准则：手机上没有悬停，必要信息不能藏在里面）。
 * 所有数字都直接写在界面上。
 */
function SummaryBreakdown({
  summary,
  currency,
  locale,
  from,
  to,
}: {
  summary: Summary;
  currency: string;
  locale: string;
  from: string;
  to: string;
}) {
  const { t } = useTranslation();
  const money = (cents: number) => formatMoney(cents, locale, currency);

  const categories = topWithRest(summary.byCategory);
  const vendors = topWithRest(summary.byVendor);
  const shareTotal = Math.max(summary.totalCents, 1);

  return (
    <Card className="mt-2 overflow-hidden">
        <div className="flex flex-col gap-6 px-5 pb-5 pt-4">
          <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
            <h3 className="text-title font-semibold text-ink">{t('purchases.breakdownTitle')}</h3>
            <p className="text-caption text-ink-3">{t('purchases.summaryHint', { count: summary.count })}</p>
          </div>

          {summary.count === 0 ? (
            <p className="-mt-3 text-body text-ink-3">{t('purchases.nothingApproved')}</p>
          ) : (
            <>
              {summary.noReceiptCount > 0 && (
                <p className="-mt-2 rounded-md bg-safety-50 px-3 py-2 text-body text-safety-600">
                  {t('purchases.noReceiptSummary', {
                    count: summary.noReceiptCount,
                    amount: money(summary.noReceiptCents),
                  })}
                </p>
              )}

              <SpendTrend byDay={summary.byDay} from={from} to={to} locale={locale} money={money} />

              <div className="grid gap-6 md:grid-cols-2 md:gap-10">
                <ShareList
                  title={t('purchases.topCategories')}
                  rows={categories.top.map((row) => ({
                    key: row.category,
                    label: row.category || t('purchases.uncategorized'),
                    muted: !row.category,
                    amountCents: row.amountCents,
                    count: row.count,
                  }))}
                  rest={
                    categories.rest && {
                      label: t('purchases.otherCategories', { count: categories.rest.groups }),
                      amountCents: categories.rest.amountCents,
                      count: categories.rest.count,
                    }
                  }
                  total={shareTotal}
                  money={money}
                />
                <ShareList
                  title={t('purchases.topVendors')}
                  rows={vendors.top.map((row) => ({
                    key: row.vendor,
                    label: row.vendor || t('purchases.noVendor'),
                    muted: !row.vendor,
                    amountCents: row.amountCents,
                    count: row.count,
                  }))}
                  rest={
                    vendors.rest && {
                      label: t('purchases.otherVendors', { count: vendors.rest.groups }),
                      amountCents: vendors.rest.amountCents,
                      count: vendors.rest.count,
                    }
                  }
                  total={shareTotal}
                  money={money}
                />
              </div>

              <div>
                <h4 className="mb-2 text-body text-ink-3">{t('purchases.byUser')}</h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-body">
                    <thead>
                      <tr className="border-b border-rule text-left text-caption uppercase tracking-wide text-ink-3">
                        <th className="py-2 pr-4 font-medium">{t('logs.submitter')}</th>
                        <th className="hidden w-20 py-2 pr-4 text-right font-medium sm:table-cell">
                          {t('purchases.colCount')}
                        </th>
                        <th className="w-32 py-2 pr-4 text-right font-medium">{t('purchases.colTotal')}</th>
                        <th className="w-32 py-2 text-right font-medium">{t('purchases.statusAPPROVED')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {summary.byUser.map((row) => (
                        <tr key={row.userId} className="border-b border-rule last:border-0">
                          <td className="py-2.5 pr-4">
                            {row.name}
                            <span className="ml-2 text-caption text-ink-3">{row.employeeNo}</span>
                          </td>
                          <td className="hidden py-2.5 pr-4 text-right tabular-nums text-ink-2 sm:table-cell">
                            {row.count}
                          </td>
                          <td className="whitespace-nowrap py-2.5 pr-4 text-right tabular-nums">
                            {money(row.amountCents)}
                          </td>
                          {/* 还欠的才上色；结清了写一横，不写 $0.00 抢眼 */}
                          <td
                            className={`whitespace-nowrap py-2.5 text-right tabular-nums ${row.outstandingCents > 0 ? 'font-semibold text-brand-700' : 'text-ink-3'}`}
                          >
                            {row.outstandingCents > 0 ? money(row.outstandingCents) : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
    </Card>
  );
}

/**
 * 支出趋势：一天一根柱子（区间太长就一个月一根）。
 *
 * 不做悬停看数 —— 手机上没有悬停。准确的数写在图上方：区间里最多的那一天和金额，
 * 以及柱高的刻度（最高那根等于多少）。每一天的精确金额本来就在下面的列表里。
 */
function SpendTrend({
  byDay,
  from,
  to,
  locale,
  money,
}: {
  byDay: Summary['byDay'];
  from: string;
  to: string;
  locale: string;
  money: (cents: number) => string;
}) {
  const { t } = useTranslation();
  const { unit, buckets } = trendBuckets(byDay, from, to);
  const max = Math.max(...buckets.map((bucket) => bucket.amountCents), 1);
  const peak = buckets.reduce((best, bucket) => (bucket.amountCents > best.amountCents ? bucket : best), buckets[0]!);

  // 日期型的值一律 UTC 渲染（代码约定），不然整列退一天
  const label = (key: string) =>
    unit === 'day'
      ? new Date(`${key}T00:00:00Z`).toLocaleDateString(locale, { timeZone: 'UTC', month: 'numeric', day: 'numeric' })
      : new Date(`${key}-01T00:00:00Z`).toLocaleDateString(locale, { timeZone: 'UTC', year: 'numeric', month: 'short' });

  return (
    <div>
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <h4 className="text-body text-ink-3">{t(unit === 'day' ? 'purchases.trendDaily' : 'purchases.trendMonthly')}</h4>
        <p className="text-caption text-ink-3">
          {t('purchases.trendPeak', { date: label(peak.key), amount: money(peak.amountCents) })}
        </p>
      </div>

      <div className="flex gap-3">
        {/* 刻度只标顶和底：最高那根是多少钱，底是 0 */}
        <div aria-hidden className="flex h-28 shrink-0 flex-col justify-between text-right text-caption tabular-nums text-ink-3">
          <span>{money(max)}</span>
          <span>0</span>
        </div>

        <div className="min-w-0 flex-1">
          <div
            role="img"
            aria-label={t('purchases.trendPeak', { date: label(peak.key), amount: money(peak.amountCents) })}
            className="flex h-28 items-end gap-px border-b border-rule"
          >
            {buckets.map((bucket) => (
              <div key={bucket.key} className="flex h-full min-w-0 flex-1 items-end">
                <div
                  className={`w-full rounded-t-sm ${bucket.key === peak.key ? 'bg-brand-600' : 'bg-brand-200'}`}
                  // 有钱的日子至少露出 2px，不然一笔小钱在图上等于没有
                  style={{ height: bucket.amountCents > 0 ? `max(2px, ${(bucket.amountCents / max) * 100}%)` : 0 }}
                />
              </div>
            ))}
          </div>

          {/* 横轴只标头、尾：几十个日期挤不下 */}
          <div aria-hidden className="mt-1 flex justify-between text-caption tabular-nums text-ink-3">
            <span>{label(buckets[0]!.key)}</span>
            {buckets.length > 1 && <span>{label(buckets[buckets.length - 1]!.key)}</span>}
          </div>
        </div>
      </div>
    </div>
  );
}

interface ShareRowData {
  label: string;
  amountCents: number;
  count: number;
  muted?: boolean;
}

/**
 * Top 类别 / Top 商家的排行榜：从高到低，金额 + 笔数（Expensify 的 Top Categories 默认就是横条图）。
 * 条的长度是占批了的总额的比例，不是和第一名比 —— 旁边写着百分比，两个得对得上。
 */
function ShareList({
  title,
  rows,
  rest,
  total,
  money,
}: {
  title: string;
  rows: Array<ShareRowData & { key: string }>;
  rest: ShareRowData | null;
  total: number;
  money: (cents: number) => string;
}) {
  const { t } = useTranslation();
  const all = rest ? [...rows, { ...rest, key: '__rest__', muted: true }] : rows;

  return (
    <div>
      <h4 className="mb-2 text-body text-ink-3">{title}</h4>
      <ul className="flex flex-col gap-3">
        {all.map((row) => {
          const share = (row.amountCents / total) * 100;
          return (
            <li key={row.key} className="text-body">
              <div className="flex items-baseline gap-3">
                <span className={`min-w-0 flex-1 truncate ${row.muted ? 'text-ink-3' : 'text-ink'}`}>
                  {row.label}
                </span>
                <span className="shrink-0 text-caption tabular-nums text-ink-3">
                  {t('purchases.countLabel', { count: row.count })}
                </span>
                <span className="w-28 shrink-0 text-right tabular-nums">{money(row.amountCents)}</span>
              </div>
              <div className="mt-1.5 flex items-center gap-3">
                <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-surface-2">
                  <div className="h-full rounded-full bg-brand-500" style={{ width: `${share}%` }} />
                </div>
                <span className="w-10 shrink-0 text-right text-caption tabular-nums text-ink-3">{percent(share)}</span>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
